import { /*Map,*/ fromJS } from "immutable";
import { handleActions, createAction } from "redux-actions";
import Restful from "lib/restful";
import GaHelper from "lib/gaHelper";
import config from "lib/fr-config";
import { sleep } from "lib/commonUtil";

const UNIT_LENGTH = config.outfitList.LENGTH;
const EXP_MINUTE = 30;
const WAIT_MILLISEC = config.storeModule.waitForLoadingMilliSec;

const SET_IS_LOADING = "othersOutfitForUser/SET_IS_LOADING";
const SET_OWNER_NAME = "othersOutfitForUser/SET_OWNER_NAME";
const INIT_EXPORTED_LIST = "othersOutfitForUser/INIT_EXPORTED_LIST";
const INIT_NEW_USER = "othersOutfitForUser/INIT_NEW_USER";
const SET_OUTFIT_LIST = "othersOutfitForUser/SET_OUTFIT_LIST";
const SET_EXPIRATION_DATE = "othersOutfitForUser/SET_EXPIRATION_DATE";
const CHECK_IS_END = "othersOutfitForUser/CHECK_IS_END";
const EXPORT_USER_DATA = "othersOutfitForUser/EXPORT_USER_DATA";
const INIT_ALL_DATA = "othersOutfitForUser/INIT_ALL_DATA";

const setIsLoading = createAction(SET_IS_LOADING);
const setOwnerName = createAction(SET_OWNER_NAME);
const initExportedList = createAction(INIT_EXPORTED_LIST);
const initNewUser = createAction(INIT_NEW_USER);
const setOutfitList = createAction(SET_OUTFIT_LIST);
const setExpirationDate = createAction(SET_EXPIRATION_DATE);
const checkIsEnd = createAction(CHECK_IS_END);
const exportUserData = createAction(EXPORT_USER_DATA);
const initAllData = createAction(INIT_ALL_DATA);

const getOthersOutfitState = ({ getState }) => {
  const othersOutfitState = getState().othersOutfitForUser.toJS();
  return othersOutfitState;
};

const getOwnerName = ({ getState }) => {
  const othersOutfitState = getOthersOutfitState({ getState });
  const { ownerName } = othersOutfitState;
  return ownerName;
};

const getIsLoading = ({ getState }) => {
  const othersOutfitState = getOthersOutfitState({ getState });
  const { isLoading } = othersOutfitState;
  return isLoading;
};

const getLastHash = list => {
  if (list.length === 0) return undefined;
  const length = list.length;
  return list[length - 1]["outfitHash"];
};

const getTargetData = ({ getState, name }) => {
  const othersOutfitState = getOthersOutfitState({ getState });
  return othersOutfitState.userObj[name];
};

const requestOutfitList = async ({ savedList, name, idToken }) => {
  const length = UNIT_LENGTH;
  const lastHash = getLastHash(savedList);
  const { code, res, errInfo } = await Restful.getOutfitOfUserAPI({
    idToken,
    name,
    lastHash,
    length
  });
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  } else {
    const { list } = res;
    return { list };
  }
};

const checkExpDate = ({ getState, name }) => {
  const targetData = getTargetData({ getState, name });
  const expirationDate = targetData && targetData.expirationDate;
  if (!expirationDate) return { needUpdate: true };
  const nowDate = new Date();
  return { needUpdate: nowDate > expirationDate ? true : false };
};

const setExpDate = ({ dispatch, name }) => {
  const expirationDate = new Date();
  expirationDate.setMinutes(expirationDate.getMinutes() + EXP_MINUTE);
  dispatch(setExpirationDate({ name, expirationDate }));
};

const addNewList = async ({ name, idToken, dispatch, getState }) => {
  const othersOutfitState = getOthersOutfitState({ getState });
  const { savedList } = othersOutfitState.userObj[name];

  const { list } = await requestOutfitList({
    savedList,
    name,
    idToken
  });

  dispatch(setOutfitList({ name, savedList: savedList.concat(list) }));
  dispatch(checkIsEnd({ name, length: list.length }));
  GaHelper.setGARequestOutfitListEvent({
    type: "othersOutfitList",
    pageNum: savedList.length / UNIT_LENGTH + 1
  });

  return null;
};

export const changeUser =
  ({ name, idToken }) =>
  async (dispatch, getState) => {
    let isLoading = getIsLoading({ getState });

    while (isLoading) {
      await sleep(WAIT_MILLISEC);
      isLoading = getIsLoading({ getState });
    }

    dispatch(setIsLoading(true));

    dispatch(setOwnerName(name));

    const { needUpdate } = checkExpDate({ getState, name });
    if (needUpdate) {
      dispatch(initExportedList());
      dispatch(initNewUser(name));
      try {
        await addNewList({ name, idToken, dispatch, getState });
      } catch (error) {
        dispatch(setIsLoading(false));
        throw error;
      }
    }

    const nowName = getOwnerName({ getState });
    if (nowName === name) {
      dispatch(exportUserData(name));
    }

    if (needUpdate) {
      setExpDate({ dispatch, name });
    }
    dispatch(setIsLoading(false));
  };

export const getList =
  ({ idToken }) =>
  async (dispatch, getState) => {
    const othersOutfitState = getOthersOutfitState({ getState });
    const { isEnd } = othersOutfitState;
    const isLoading = getIsLoading({ getState });

    if (isLoading || isEnd) return null;

    dispatch(setIsLoading(true));
    const name = getOwnerName({ getState });
    try {
      await addNewList({ name, idToken, dispatch, getState });
    } catch (error) {
      dispatch(setIsLoading(false));
      throw error;
    }

    const nowName = getOwnerName({ getState });
    if (nowName === name) {
      dispatch(exportUserData(name));
    }

    dispatch(setIsLoading(false));
  };

export const deleteAllData = () => (dispatch, getState) => {
  dispatch(initAllData());
};

const initialState = fromJS({
  userObj: {},
  /*
  userObj 의 구조.
  userObj: {
    dummyUsersName: {
      savedList: [],
      expirationDate: null,
      isEnd: false
    }
  },
  */

  ownerName: null,

  outfitList: [],
  isEnd: false,
  isLoading: false
});

export default handleActions(
  {
    [SET_IS_LOADING]: (state, action) => {
      const isLoading = action.payload;
      return state.set("isLoading", fromJS(isLoading));
    },
    [SET_OWNER_NAME]: (state, action) => {
      const ownerName = action.payload;
      return state.set("ownerName", fromJS(ownerName));
    },
    [INIT_EXPORTED_LIST]: (state, action) => {
      const { outfitList } = initialState.toJS();
      return state.set("outfitList", fromJS(outfitList));
    },
    [INIT_NEW_USER]: (state, action) => {
      const name = action.payload;
      const { userObj } = state.toJS();
      userObj[name] = {
        savedList: [],
        expirationDate: null,
        isEnd: false
      };
      return state.set("userObj", fromJS(userObj));
    },
    [SET_OUTFIT_LIST]: (state, action) => {
      const { name, savedList } = action.payload;
      return state.setIn(["userObj", name, "savedList"], fromJS(savedList));
    },
    [SET_EXPIRATION_DATE]: (state, action) => {
      const { name, expirationDate } = action.payload;
      return state.setIn(
        ["userObj", name, "expirationDate"],
        fromJS(expirationDate)
      );
    },
    [CHECK_IS_END]: (state, action) => {
      const { name, length } = action.payload;
      const isEnd = length === UNIT_LENGTH ? false : true;
      return state.setIn(["userObj", name, "isEnd"], fromJS(isEnd));
    },
    [EXPORT_USER_DATA]: (state, action) => {
      const name = action.payload;
      const { savedList, isEnd } = state.toJS().userObj[name];
      return state
        .set("outfitList", fromJS(savedList))
        .set("isEnd", fromJS(isEnd));
    },
    [INIT_ALL_DATA]: (state, action) => {
      return initialState;
    }
  },
  initialState
);
